import React, { useState, useEffect } from 'react'
import {
  Link2,
  Plus,
  Trash2,
  Edit2,
  ExternalLink,
  Copy,
  Check,
  Video,
  Clock,
  Calendar,
  AlertCircle,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'
import { useAuth } from '@/lib/auth/context'
import { useBookingPages, useBookingPageMutations } from '@/lib/data/hooks/useBooking'
import { PublicBookingPage, CreateBookingPageInput } from '@/lib/data/hooks/useBooking'

const DURATION_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
]

const BUFFER_OPTIONS = [
  { value: 0, label: 'No buffer' },
  { value: 5, label: '5 minutes' },
  { value: 10, label: '10 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
]

const NOTICE_OPTIONS = [
  { value: 1, label: '1 hour' },
  { value: 2, label: '2 hours' },
  { value: 4, label: '4 hours' },
  { value: 24, label: '1 day' },
  { value: 48, label: '2 days' },
  { value: 72, label: '3 days' },
  { value: 168, label: '1 week' },
]

const MAX_DAYS_OPTIONS = [
  { value: 7, label: '1 week' },
  { value: 14, label: '2 weeks' },
  { value: 30, label: '1 month' },
  { value: 60, label: '2 months' },
  { value: 90, label: '3 months' },
]

export function BookingPageSettings() {
  const { adminUser } = useAuth()
  const { bookingPages, isLoading, refetch } = useBookingPages()
  const { createBookingPage, updateBookingPage, toggleBookingPage, deleteBookingPage, isLoading: saving, error } = useBookingPageMutations()

  const [showForm, setShowForm] = useState(false)
  const [editingPage, setEditingPage] = useState<PublicBookingPage | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // Form state
  const [formData, setFormData] = useState<CreateBookingPageInput>({
    slug: '',
    title: 'Book a Meeting',
    description: '',
    defaultDuration: 30,
    bufferBefore: 0,
    bufferAfter: 15,
    minNoticeHours: 24,
    maxDaysAhead: 30,
    allowVideoConference: true,
    requireVideoConference: false,
  })

  useEffect(() => {
    if (editingPage) {
      setFormData({
        slug: editingPage.slug,
        title: editingPage.title,
        description: editingPage.description || '',
        defaultDuration: editingPage.defaultDuration,
        bufferBefore: editingPage.bufferBefore,
        bufferAfter: editingPage.bufferAfter,
        minNoticeHours: editingPage.minNoticeHours,
        maxDaysAhead: editingPage.maxDaysAhead,
        allowVideoConference: editingPage.allowVideoConference,
        requireVideoConference: editingPage.requireVideoConference,
      })
      setShowForm(true)
    }
  }, [editingPage])

  const resetForm = () => {
    setFormData({
      slug: '',
      title: 'Book a Meeting',
      description: '',
      defaultDuration: 30,
      bufferBefore: 0,
      bufferAfter: 15,
      minNoticeHours: 24,
      maxDaysAhead: 30,
      allowVideoConference: true,
      requireVideoConference: false,
    })
    setEditingPage(null)
    setShowForm(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (editingPage) {
      await updateBookingPage(editingPage.id, formData)
    } else {
      await createBookingPage(formData)
    }

    resetForm()
    refetch()
  }

  const handleToggle = async (page: PublicBookingPage) => {
    await toggleBookingPage(page.id, !page.isActive)
    refetch()
  }

  const handleDelete = async (page: PublicBookingPage) => {
    if (confirm(`Are you sure you want to delete "${page.title}"?`)) {
      await deleteBookingPage(page.id)
      refetch()
    }
  }

  const getBookingUrl = (slug: string) => {
    return `${window.location.origin}/book/${slug}`
  }

  const copyToClipboard = async (slug: string, id: string) => {
    await navigator.clipboard.writeText(getBookingUrl(slug))
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[var(--text-main)] mb-1">
            Booking Pages
          </h3>
          <p className="text-[var(--text-muted)] text-sm">
            Create public pages where people can schedule meetings with you
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-opacity"
          >
            <Plus size={16} />
            New Booking Page
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-500">
          <AlertCircle size={16} />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-[var(--bg-main)] rounded-xl border border-[var(--border)] p-6 space-y-6">
          <h4 className="font-medium text-[var(--text-main)]">
            {editingPage ? 'Edit Booking Page' : 'Create New Booking Page'}
          </h4>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-1">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => {
                  setFormData({ ...formData, title: e.target.value })
                  if (!editingPage) {
                    setFormData(prev => ({ ...prev, slug: generateSlug(e.target.value) }))
                  }
                }}
                placeholder="Book a Meeting"
                required
                className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-1">
                URL Slug
              </label>
              <div className="flex items-center">
                <span className="px-3 py-2 bg-[var(--bg-element)] border border-r-0 border-[var(--border)] rounded-l-lg text-[var(--text-muted)] text-sm">
                  /book/
                </span>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  placeholder="your-meeting"
                  required
                  pattern="^[a-z0-9-]+$"
                  className="flex-1 px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-r-lg text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-1">
              Description (optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this meeting is about..."
              rows={2}
              className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50 resize-none"
            />
          </div>

          {/* Scheduling Options */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-1">
                <Clock size={14} className="inline mr-1" />
                Duration
              </label>
              <select
                value={formData.defaultDuration}
                onChange={(e) => setFormData({ ...formData, defaultDuration: parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50"
              >
                {DURATION_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-1">
                Buffer Before
              </label>
              <select
                value={formData.bufferBefore}
                onChange={(e) => setFormData({ ...formData, bufferBefore: parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50"
              >
                {BUFFER_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-1">
                Buffer After
              </label>
              <select
                value={formData.bufferAfter}
                onChange={(e) => setFormData({ ...formData, bufferAfter: parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50"
              >
                {BUFFER_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-1">
                Min Notice
              </label>
              <select
                value={formData.minNoticeHours}
                onChange={(e) => setFormData({ ...formData, minNoticeHours: parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50"
              >
                {NOTICE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-1">
                <Calendar size={14} className="inline mr-1" />
                Max Days Ahead
              </label>
              <select
                value={formData.maxDaysAhead}
                onChange={(e) => setFormData({ ...formData, maxDaysAhead: parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50"
              >
                {MAX_DAYS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Video Conference Options */}
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.allowVideoConference}
                onChange={(e) => setFormData({ ...formData, allowVideoConference: e.target.checked })}
                className="w-4 h-4 rounded border-[var(--border)] text-[var(--primary)] focus:ring-[var(--primary)]"
              />
              <Video size={16} className="text-[var(--text-muted)]" />
              <span className="text-sm text-[var(--text-main)]">Include video conference link</span>
            </label>

            {formData.allowVideoConference && (
              <label className="flex items-center gap-3 cursor-pointer ml-7">
                <input
                  type="checkbox"
                  checked={formData.requireVideoConference}
                  onChange={(e) => setFormData({ ...formData, requireVideoConference: e.target.checked })}
                  className="w-4 h-4 rounded border-[var(--border)] text-[var(--primary)] focus:ring-[var(--primary)]"
                />
                <span className="text-sm text-[var(--text-muted)]">Make video conference required</span>
              </label>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4 border-t border-[var(--border)]">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingPage ? 'Update Page' : 'Create Page'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Existing Booking Pages */}
      {bookingPages.length === 0 && !showForm ? (
        <div className="text-center py-12 bg-[var(--bg-main)] rounded-xl border border-[var(--border)]">
          <Link2 size={32} className="mx-auto mb-3 text-[var(--text-muted)] opacity-50" />
          <p className="text-[var(--text-muted)] mb-4">No booking pages yet</p>
          <button
            onClick={() => setShowForm(true)}
            className="text-[var(--primary)] hover:underline"
          >
            Create your first booking page
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {bookingPages.map(page => (
            <div
              key={page.id}
              className={`bg-[var(--bg-main)] rounded-xl border border-[var(--border)] p-4 transition-opacity ${
                !page.isActive ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-[var(--text-main)] truncate">{page.title}</h4>
                    {!page.isActive && (
                      <span className="px-2 py-0.5 text-xs bg-[var(--bg-element)] text-[var(--text-muted)] rounded-full">
                        Inactive
                      </span>
                    )}
                  </div>
                  {page.description && (
                    <p className="text-sm text-[var(--text-muted)] mb-2 truncate">{page.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-[var(--text-muted)]">
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {page.defaultDuration} min
                    </span>
                    {page.allowVideoConference && (
                      <span className="flex items-center gap-1">
                        <Video size={14} />
                        Video
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {/* Toggle */}
                  <button
                    onClick={() => handleToggle(page)}
                    className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
                    title={page.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {page.isActive ? <ToggleRight size={20} className="text-[var(--primary)]" /> : <ToggleLeft size={20} />}
                  </button>

                  {/* Copy Link */}
                  <button
                    onClick={() => copyToClipboard(page.slug, page.id)}
                    className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
                    title="Copy link"
                  >
                    {copiedId === page.id ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                  </button>

                  {/* Open in new tab */}
                  <a
                    href={getBookingUrl(page.slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
                    title="Open page"
                  >
                    <ExternalLink size={16} />
                  </a>

                  {/* Edit */}
                  <button
                    onClick={() => setEditingPage(page)}
                    className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
                    title="Edit"
                  >
                    <Edit2 size={16} />
                  </button>

                  {/* Delete */}
                  <button
                    onClick={() => handleDelete(page)}
                    className="p-2 text-[var(--text-muted)] hover:text-red-500 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* URL Preview */}
              <div className="mt-3 pt-3 border-t border-[var(--border)]">
                <code className="text-xs text-[var(--primary)] bg-[var(--bg-element)] px-2 py-1 rounded">
                  {getBookingUrl(page.slug)}
                </code>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
